import { Injectable, inject } from '@angular/core';
import {
  ApiDependencyProviderService,
  CollectionRequest,
  CollectionResponse,
  HttpParams,
} from '@web/common';
import { map, Observable } from 'rxjs';
import { blank, Guid, valueOrDefault } from '@web/utils';
import { AdminPaymentMethodCollectionItemResponse } from '../responses/admin-payment-method-collection-item.response';
import { AdminPaymentMethodDetailsResponse } from '../responses/admin-payment-method-details.response';
import { AdminPaymentMethodUpdateRequest } from '../requests/admin-payment-method-update.request';

@Injectable({
  providedIn: 'root',
})
export class AdminPaymentApiService {
  private readonly provider = inject(ApiDependencyProviderService);

  public fetchCollection(options: {
    request: CollectionRequest;
    cacheable?: boolean;
    skipByLoading?: boolean;
  }): Observable<CollectionResponse<AdminPaymentMethodCollectionItemResponse>> {
    const path = 'admin/payment/method';
    const url = this.provider.httpUrlFactory.withQueryParams(
      this.hostUrl(),
      path,
      this.provider.httpHelper.collectionRequestToQueryParams(options.request),
    );
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .withOptionalCacheInterceptor(() =>
        valueOrDefault(options?.cacheable, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToCollectionResponse(
            AdminPaymentMethodCollectionItemResponse,
          ),
        ),
      );
  }

  public fetchDetailsById(options: {
    id: Guid;
    skipByLoading: boolean;
  }): Observable<AdminPaymentMethodDetailsResponse> {
    const path = `admin/payment/method/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, {
      [HttpParams.Id]: options.id,
    });
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .withOptionalSkipByLoadingInterceptor(() =>
        valueOrDefault(options?.skipByLoading, false),
      )
      .build();

    return this.provider.http
      .get(url, { context })
      .pipe(
        map(
          this.provider.httpHelper.bodyToResponse(
            AdminPaymentMethodDetailsResponse,
          ),
        ),
      );
  }

  /* prettier-ignore */
  public update(id: Guid, request: AdminPaymentMethodUpdateRequest): Observable<void> {
    const path = `admin/payment/method/${HttpParams.Id}`;
    const url = this.provider.httpUrlFactory.withParams(this.hostUrl(), path, { [HttpParams.Id]: id });
    const body = this.provider.httpHelper.requestToBody(request, AdminPaymentMethodUpdateRequest);
    const context = this.provider.httpContextBuilder
      .skipByHttpErrorInterceptor()
      .build();

    return this.provider.http.put(url, body, { context }).pipe(blank());
  }

  private hostUrl(): string {
    return this.provider.environment.ApiHostUrl.Shop;
  }
}
